import { AxiosError } from 'axios';
import { isArray, uniq } from 'lodash';
import qs from 'query-string';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GLOBAL_PER_PAGE } from 'routes/constants';
import {
  LocalStorageItemKeyForFields,
  LocalStorageItemKeyForOrder,
} from '../routes/screens/settings/screens/customfields/constants';
import {
  AccountInfoType,
  ContactFormType,
  FormServFieldType,
  LoginResponseType,
  LoginRquestType,
  MutationType,
  PlaceholderValuesType,
  QueueFilterType,
  RuleType,
  SaveContactFormFieldType,
  SegmentResponseType,
  SurveyResponseType,
  SurveyServAuthType,
  SurveyType,
  SurveysResponseType,
  SurveysResponsesResponseType,
  TransactionFieldsType,
} from './types';
import request from '../utils/request';
import { getRulePayload, getSurveyFormValues } from './utils';
import {
  BACK_END_FIELDS,
  COLUMNS_HIDE_ORDERING_KEY,
  TEMPORARY_HIDDEN_FIELDS,
} from './constants';
import i18n from '../i18n';

export const useGetAccountInfo = ({
  refetchOnWindowFocus = false,
  enabled = true,
  callback,
}: MutationType) => {
  return useQuery(
    ['accountinfo'],
    async () => {
      const data: AccountInfoType = await request('/accounts/mine');
      return data;
    },
    {
      enabled,
      refetchOnWindowFocus,
      staleTime: refetchOnWindowFocus ? 10000 : 300000,
      onSuccess: (response: AccountInfoType) => {
        if (response && response.mcr_account_id) {
          window.localStorage.setItem(
            'mcr-account-id',
            response.mcr_account_id,
          );
        }
        if (callback) callback(true, response);
      },
    },
  );
};

export const useLogin = ({ setToast, callback }: MutationType) => {
  return useMutation(
    async (payload: LoginRquestType) => {
      const data: LoginResponseType = await request('/login', payload, 'POST');
      return data;
    },
    {
      onSuccess: (response, payload) => {
        if (callback) callback(true, response, payload);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.login_failed')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useUpdateAccountInfo = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: AccountInfoType) => {
      const data: AccountInfoType = await request(
        '/accounts/mine',
        payload,
        'PATCH',
      );
      return data;
    },
    {
      onSuccess: (response: AccountInfoType) => {
        queryClient.setQueryData(['accountinfo'], () => response);
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.account_info_updated')}`,
          });
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.account_information_update')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const usePostAddDkim = ({ setToast, callback }: MutationType) => {
  return useMutation(
    async () => {
      const data: AccountInfoType = await request(
        '/accounts/mine/dkim',
        {},
        'POST',
      );
      return data;
    },
    {
      onSuccess: () => {
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.dkim_added')}`,
          });
        if (callback) callback(true);
      },
      onError: () => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.dkim_add')}`,
          });
      },
    },
  );
};

export const useGetContactForm = ({
  callback,
  enabled = true,
}: MutationType = {}) => {
  return useQuery(
    'contactForm',
    async () => {
      const data: ContactFormType = await request('/contacts/forms/');
      return data;
    },
    {
      enabled,
      onSuccess: (response: ContactFormType) => {
        // If the form updated_at is different check
        // all the local values and remove any missing ids
        const formUpdated = localStorage.getItem('form-updated');
        const migrationVersion = localStorage.getItem('migration-version');
        if (formUpdated !== response.updated_at || !migrationVersion) {
          const { fields = [] } = response || {};
          const allFieldIds: string[] = [];
          fields.forEach((field) => {
            if (field[COLUMNS_HIDE_ORDERING_KEY]) {
              allFieldIds.push(field[COLUMNS_HIDE_ORDERING_KEY]);
            }
          });
          [
            ...LocalStorageItemKeyForFields,
            ...LocalStorageItemKeyForOrder,
          ].forEach((key) => {
            const localStorgeItem = localStorage.getItem(key);
            try {
              if (localStorgeItem) {
                const localStorgeValue: string[] = localStorgeItem
                  ? JSON.parse(localStorgeItem)
                  : [];
                if (LocalStorageItemKeyForFields.includes(key)) {
                  fields.forEach((field) => {
                    if (
                      [...TEMPORARY_HIDDEN_FIELDS, ...BACK_END_FIELDS].includes(
                        field[COLUMNS_HIDE_ORDERING_KEY],
                      )
                    ) {
                      localStorgeValue.push(field[COLUMNS_HIDE_ORDERING_KEY]);
                    }
                  });
                }
                const valueToSet = uniq(
                  localStorgeValue.filter((val) => allFieldIds.includes(val)),
                );
                if (
                  !valueToSet ||
                  (isArray(valueToSet) && valueToSet.length === 0)
                ) {
                  localStorage.removeItem(key);
                } else {
                  localStorage.setItem(key, JSON.stringify(valueToSet));
                }
              }
            } catch (e) {}
          });
          localStorage.setItem('form-updated', `${response.updated_at || ''}`);
        }
        if (!formUpdated) {
          localStorage.setItem('form-updated', `${response.updated_at || ''}`);
        }
        if (!migrationVersion) {
          localStorage.setItem('migration-version', '1');
        }
        if (callback) callback(true, response);
      },
    },
  );
};

export const useSaveContactForm = ({ setToast }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (contactForm: ContactFormType) => {
      const data = await request('/contacts/forms/', contactForm, 'POST');
      return data;
    },
    {
      onSuccess: (response) => {
        queryClient.setQueryData('contactForm', response);
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.contact_form_saved')}`,
          });
      },
      onError: () => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.contact_form_saved')}`,
          });
      },
    },
  );
};

export const useSaveContactFormField = ({
  setToast,
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation<FormServFieldType, any, SaveContactFormFieldType>(
    async ({ payload, id }) => {
      const data: FormServFieldType = await request(
        id ? `/contacts/forms/fields/${id}/` : '/contacts/forms/fields/',
        payload,
        id ? 'PUT' : 'POST',
      );
      return data;
    },
    {
      onSuccess: (response, variables) => {
        const { id, payload } = variables;
        if (id || payload.created_at) {
          queryClient.setQueryData('contactForm', (form?: ContactFormType) => {
            const newForm: ContactFormType = {
              ...(form || {}),
              fields: [...(form?.fields || [])].map((field) =>
                field.id === response.id ? response : field,
              ),
            };
            return newForm;
          });
        } else {
          queryClient.setQueryData('contactForm', (form?: ContactFormType) => {
            const newForm: ContactFormType = {
              ...(form || {}),
              fields: [...(form?.fields || []), response],
            };
            return newForm;
          });
          // Need invalidation because field will have additional data after saving
          queryClient.invalidateQueries('contactForm');
        }
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: id
              ? `${i18n.t('forms.toast.success.field_saved')}`
              : `${i18n.t('forms.toast.success.field_created')}`,
          });
        if (callback) callback(true, response, variables);
      },
      onError: (error, variables) => {
        const { id } = variables;
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: id
              ? `${i18n.t('forms.toast.error.field_save')}`
              : `${i18n.t('forms.toast.error.field_created')}`,
          });
        if (callback) callback(false, error, variables);
      },
    },
  );
};

export const useDeleteContactFormField = ({
  setToast,
  callback,
}: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation<string | undefined, any, FormServFieldType>(
    async (field: FormServFieldType) => {
      if (!field.id) return;
      return await request(`/contacts/forms/fields/${field.id}/`, {}, 'DELETE');
    },
    {
      onSuccess: (response, field) => {
        queryClient.setQueryData('contactForm', (form?: ContactFormType) => {
          const newForm: ContactFormType = {
            ...(form || {}),
            fields: [...(form?.fields || [])].filter((f) => f.id !== field.id),
          };
          return newForm;
        });
        // Need invalidation because the position of remaining fields will change
        queryClient.invalidateQueries('contactForm');
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.field_deleted')}`,
          });
        if (callback) callback(true, response, field);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.field_deleted')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

type ExecuteRuleType = {
  data?: any;
  callback?: (success: boolean, response?: any, payload?: any) => void;
  rule: RuleType;
  params: {
    page?: number;
    per_page?: number;
  };
};

export const useExecuteSegment = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ rule, params }: ExecuteRuleType) => {
      const { page = 1, per_page = GLOBAL_PER_PAGE } = params || {};
      const modifiedRule = getRulePayload(rule || {});
      const data: SegmentResponseType = await request(
        `/contacts/segments/execute?per_page=${per_page}&page=${page}`,
        modifiedRule,
        'POST',
      );
      return data;
    },
    {
      onSuccess: (response, payload) => {
        if (response?.contacts?.length > 0) {
          response.contacts.forEach((contact) => {
            queryClient.setQueryData(['contact', `${contact.id}`], contact);
          });
        }
        if (payload.callback) payload.callback(true, response, payload);
        if (callback) callback(true, response, payload);
      },
      onError: () => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.data_fetching')}`,
          });
        if (callback) callback(false);
      },
    },
  );
};

export const useExportSurvey = ({
  callback,
  surveyId,
  as,
  enabled = true,
}: MutationType & {
  surveyId: string;
  as: 'survey' | 'template';
}) =>
  useQuery<Blob>(
    ['survey-export', surveyId],
    async () =>
      request(
        `/surveyserv/${as}s/${surveyId}/export`,
        undefined,
        'GET',
        undefined,
        'arraybuffer',
      ),
    {
      enabled,
      onSuccess: (response: Blob) => {
        if (callback) callback(true, response);
      },
      onError: (error) => {
        const { response } = (error as AxiosError) ?? {};
        let errorMsg = `forms.toast.error.export_${as}_failed`;

        if (response?.status === 429) {
          errorMsg = `forms.toast.error.export_${as}_failed_rate_limit`;
        }
        if (callback) callback(false, errorMsg);
      },
      staleTime: 0,
      retry: false,
    },
  );

export const useUploadCsv = ({ setToast, callback }: MutationType) => {
  return useMutation(
    async (csvFile: Blob) => {
      const bodyFormData = new FormData();
      bodyFormData.append('file', csvFile);
      const data = await request('/contacts/import/', bodyFormData, 'POST', {
        'Content-Type': 'multipart/form-data',
      });
      return data;
    },
    {
      onSuccess: (response, payload) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('success')}`,
          });
        if (callback) callback(true, response, payload);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('failed')}`,
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useGetSurveys = ({ callback, enabled = true }: MutationType) => {
  return useQuery(
    'surveys',
    async () => {
      const data: SurveysResponseType = await request(
        '/surveyserv/surveys/?page_size=100',
      );
      return data;
    },
    {
      enabled,
      onSuccess: (response: SurveysResponseType) => {
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
    },
  );
};

export const useGetSurvey = ({
  surveyId,
  callback,
  refetchOnMount,
  versionId,
}: MutationType & { surveyId: string; versionId?: any }) => {
  return useQuery<SurveyType>(
    ['survey', surveyId],
    async () => {
      const data: SurveyType = versionId
        ? await request(`/surveys/${surveyId}/versions/${versionId}`)
        : await request(`/surveyserv/surveys/${surveyId}`);
      return getSurveyFormValues({ survey: data });
    },
    {
      refetchOnMount,
      enabled: Boolean(surveyId),
      refetchOnWindowFocus: true,
      cacheTime: 0,
      onSuccess: (response: any) => {
        if (callback) callback(true, response);
      },
      onError: (err) => {
        if (callback) callback(false, err);
      },
    },
  );
};

export const useGetSurveyResponses = ({
  callback,
  ucrContactId,
  surveyIds,
  contactId,
  enabled = true,
}: MutationType & {
  ucrContactId?: string;
  contactId?: string;
  surveyIds: string[];
}) => {
  return useQuery(
    ['responses', ucrContactId],
    async () => {
      const queryParams = {
        ...(ucrContactId ? { ucr_contact_id: ucrContactId } : {}),
        ...(contactId ? { contact_id: contactId } : {}),
      };
      const data: SurveyResponseType[] = [];
      if (surveyIds.length === 0) return data;
      const response: SurveysResponsesResponseType[] = await Promise.all(
        surveyIds
          .filter((surveyId) => !!surveyId)
          .map((surveyId) =>
            request(
              `/surveys/${surveyId}/responses?${qs.stringify(queryParams, {
                skipEmptyString: true,
              })}&status=COMPLETE&status=PARTIAL_COMPLETE`,
            ),
          ),
      );
      if (response && response.length > 0)
        response.forEach((resp: SurveysResponsesResponseType) => {
          if (resp.surveyResponses) {
            data.push(...(resp.surveyResponses || []));
          }
        });
      return data;
    },
    {
      enabled,
      onSuccess: (response: SurveyResponseType[]) => {
        if (callback) callback(true, response);
      },
    },
  );
};

export const useGetSurveyResponseById = ({
  callback,
  responseId,
  surveyId,
}: MutationType & {
  responseId: string;
  surveyId: string;
}) => {
  return useQuery<SurveyResponseType>(
    ['responses', responseId],
    async () => {
      const response: SurveyResponseType = await request(
        `/surveys/${surveyId}/responses/${responseId}?status=COMPLETE&status=PARTIAL_COMPLETE`,
      );
      return response;
    },
    {
      enabled: Boolean(responseId) && Boolean(surveyId),
      onSuccess: (response: SurveyResponseType) => {
        if (callback) callback(true, response);
      },
    },
  );
};

export const useGetPlaceholderValues = (survey_id: string) => {
  return useQuery(
    'placeholder-values',
    async () => {
      if (!survey_id) return;
      const data: PlaceholderValuesType = await request(
        '/surveyserv/placeholder-values',
        { survey_id },
        'POST',
      );
      return data;
    },
    {
      enabled: Boolean(survey_id),
    },
  );
};

export const useGetAuthData = () => {
  return useQuery(
    'surveyserv-auth-info',
    async () => {
      const data: SurveyServAuthType = await request('/surveyserv/auth');
      return data;
    },
    {
      onSuccess: (response: SurveyServAuthType) => {
        window.localStorage.setItem('survey:token', response.jwt_token);
      },
      staleTime: 0,
    },
  );
};

export const useGetTransactionFields = ({
  surveyId,
  enabled = true,
}: MutationType & {
  surveyId: string | undefined | null;
}) =>
  useQuery(
    `transaction-fields-${surveyId}`,
    async () => {
      const data: TransactionFieldsType = await request(
        `/surveyserv/surveys/${surveyId}/transaction-fields/`,
      );
      return data;
    },
    {
      enabled,
      staleTime: 0,
    },
  );

export const useGetTransactionDetailsBulk = ({
  transactionIds,
  surveyId,
  callback,
  enabled,
}: MutationType & {
  transactionIds: string[];
  surveyId: string;
}) =>
  useQuery(
    'transaction-info',
    async () => {
      const data = await request(
        `/surveyserv/surveys/${surveyId}/transactions/bulk`,
        transactionIds,
        'POST',
      );
      return data?.survey_transactions || [];
    },
    {
      enabled,
      onSuccess: () => {
        if (callback) callback(true);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
      staleTime: 0,
    },
  );

export const useGetTriggersCount = ({
  surveyId,
  filters,
  enabled = true,
}: MutationType & {
  surveyId: string;
  filters: QueueFilterType;
}) =>
  useQuery(
    `triggers-count-${surveyId}`,
    async () => {
      const { filterParams = {} } = filters;
      const filterPayload = {
        survey_id: surveyId,
        ...filterParams,
      };
      return await request(
        `/surveyserv/surveys/${surveyId}/transaction-triggers/count`,
        filterPayload,
        'POST',
      );
    },
    {
      enabled: enabled && !!surveyId,
      refetchOnWindowFocus: true,
      staleTime: 30000,
    },
  );
